// Toast surface — FIFO queue, max 3 visible, per-variant auto-dismiss.
// Per W2 §3.3.2 #10 (copy v2 verbatim) + §7.5: ship from start, never retrofit.
//
// Variants (per InfoBanner tones + system error):
//   - default  : paper-deep bg, ink-1 text (neutral notice)
//   - you      : you-tint bg (positive / paired-with-you)
//   - partner  : partner-tint bg
//   - shared   : shared-tint bg
//   - danger   : partner-tint bg, ink-1 text (errors, validation failures)
//   - success  : gold-tint bg (registered, verified, etc.)

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Check, Info, TriangleAlert, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export type ToastVariant = "default" | "you" | "partner" | "shared" | "danger" | "success";

export interface ToastInput {
  message: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ActiveToast extends ToastInput {
  id: number;
}

interface ToastContextValue {
  push: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 3;

/**
 * Auto-dismiss per variant, in milliseconds. An explicit `durationMs` on the
 * call always wins, and `durationMs: 0` still means 「pinned」.
 *
 *   danger   6000 — an error has to be *read*. The old flat 2500ms meant
 *                    「還原失敗：這個心願已被其他裝置刪除，請重新整理頁面後再試一次」
 *                    was gone before it could be read.
 *   default  3500 — a neutral notice is worth more than a blink.
 *   success / you / partner / shared  2500 — confirmation, unchanged.
 *
 * There is no `info` variant: `Info` is the *icon* the default / you / partner
 * / shared variants carry, and an icon has no duration of its own. The
 * duration belongs to the variant, so `default` gets 3500.
 */
const VARIANT_DURATION_MS: Record<ToastVariant, number> = {
  default: 3500,
  you: 2500,
  partner: 2500,
  shared: 2500,
  danger: 6000,
  success: 2500,
};

/**
 * How long a message has to be before it is *offered* the expansion control
 * when the text cannot be measured. Two lines of `text-body-sm` (14px) in the
 * card's 260–420px text column hold roughly 24–50 characters, so 20 is the
 * conservative end. This only decides whether the affordance is *offered* —
 * the clamp and the fade are driven by the real measurement.
 */
const UNMEASURED_CLAMP_MIN_CHARS = 20;

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  default: "bg-paper-deep text-ink-1 border-hairline",
  you: "bg-you-tint text-ink-1 border-you/30",
  partner: "bg-partner-tint text-ink-1 border-partner/30",
  shared: "bg-shared-tint text-ink-1 border-shared/30",
  // The project's own tokens, not Tailwind's default palette: `bg-red-50` /
  // `border-red-200` / `text-red-600` and the emerald trio are colours that
  // exist nowhere in this palette — and the hard-rule audit only catches raw
  // hex, so it called this file clean and they survived.
  danger: "bg-partner-tint text-ink-1 border-partner/30",
  success: "bg-gold-tint text-ink-1 border-gold/30",
};

const VARIANT_ICONS: Record<ToastVariant, ReactNode> = {
  default: <Info className="w-4 h-4" aria-hidden />,
  you: <Info className="w-4 h-4" aria-hidden />,
  partner: <Info className="w-4 h-4" aria-hidden />,
  shared: <Info className="w-4 h-4" aria-hidden />,
  danger: <TriangleAlert className="w-4 h-4 text-partner-strong" aria-hidden />,
  success: <Check className="w-4 h-4 text-gold-ink" aria-hidden />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const counterRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (toast: ToastInput): number => {
      counterRef.current += 1;
      const id = counterRef.current;
      const variant = toast.variant ?? "default";
      const entry: ActiveToast = {
        ...toast,
        variant,
        id,
      };
      setToasts((prev) => {
        // FIFO: drop the oldest if we're at MAX_VISIBLE.
        const next = [...prev, entry];
        if (next.length > MAX_VISIBLE) {
          const dropped = next.slice(0, next.length - MAX_VISIBLE);
          dropped.forEach((d) => {
            const timer = timersRef.current.get(d.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(d.id);
            }
          });
          return next.slice(-MAX_VISIBLE);
        }
        return next;
      });
      // The explicit value always wins; `??` (not `||`) keeps `durationMs: 0`
      // meaning "pinned" instead of falling back to the variant default.
      const duration = toast.durationMs ?? VARIANT_DURATION_MS[variant];
      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        data-component="ToastViewport"
        // **Top-right**, per DESIGN.md §11.1 #6, which places Toast/InfoBanner at
        // 「top right」 at `z-60`.
        //
        // It used to be `bottom-4 inset-x-0 ... items-center` — bottom centre —
        // and that put every toast directly over the PillNav. Measured with a
        // toast actually on screen (an empty viewport is a zero-height flex
        // container, so the first version of that check reported "no overlap" for
        // a viewport that in fact sat exactly where the nav is): the toast's box
        // was y 784–828 against the nav's 774–834 — **13,112px² of overlap**. At
        // the old z-50 the nav simply painted over it; at z-60, which this fix
        // needs for the sheet, the toast paints over the *primary navigation*
        // instead. `top` rather than `bottom` also clears the undo bars, which
        // dock at `bottom-[108px]` (Calendar / Wishes / DiaryDetail).
        //
        // The offset clears the sticky `MobileHeader` (`h-11`, `z-20`) so a toast
        // never sits on the bell or the avatar, and carries the notch inset
        // because the header does — a fixed overlay that ignores
        // `safe-area-inset-top` hides itself under the status bar on exactly the
        // devices with one.
        className="fixed right-0 top-[calc(env(safe-area-inset-top)+3.25rem)] z-60 flex flex-col items-end gap-2 pointer-events-none px-4"
        // `z-60`, not `z-50`. §11.1 #6 puts Toast above Modal (z-50) and Mobile
        // BottomSheet (z-40); the implementation had it level with the sheet.
        // Equal z-index is resolved by DOM order, and `Dialog.Portal` appends to
        // the end of `<body>`, so a toast raised while a sheet was open painted
        // **underneath** the sheet and was never seen.
        //
        // `z-60` only became a real value in W46.0 — Tailwind's default scale
        // stops at 50, so `z-60` emitted nothing and the element fell back to
        // `z-index: auto`, which stacks by DOM order and so loses to every
        // positioned sibling. It read as correct in the source and in review; only
        // reading the computed value in a real browser
        // (`scripts/mobile-overlay-gesture-audit.mjs`) showed it.
        //
        // **No `aria-live` here.** The viewport used to be a polite live region
        // *and* every card inside it carried `role="status"` (itself a polite
        // live region) — two nested live regions, which can announce the same
        // toast twice. The card is the one live region; the viewport is just a
        // positioned box.
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ActiveToast; onDismiss: () => void }) {
  const variant = toast.variant ?? "default";
  return (
    <div
      data-testid="toast"
      data-variant={variant}
      role="status"
      // The live region is the **card**, not the viewport (see the note there).
      // `role="status"` already implies `aria-live="polite"`; it is written out
      // because a screen reader only hears a message that is *declared* to be
      // one.
      aria-live="polite"
      className={cn(
        "pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-md border shadow-sm",
        "min-w-[260px] max-w-[420px] text-body-sm",
        "animate-[fadeIn_120ms_ease-out]",
        VARIANT_CLASSES[variant],
      )}
    >
      {VARIANT_ICONS[variant]}
      <ToastMessage message={toast.message} />
      <button
        type="button"
        onClick={onDismiss}
        data-testid="toast-dismiss"
        aria-label="關閉"
        // The `::after` overlay, same axis split as `.shell-btn`: a 14px icon in
        // `p-1` is a 22×22 box, which is 22px under the 44pt floor in **both**
        // axes. Growing the button to 44 would change the card's row rhythm on
        // every toast, so the hit area is an absolutely-positioned pseudo-element
        // instead — zero layout impact, real 44×44 target.
        //
        // This went unnoticed for a long time because it is *invisible to the
        // route sweep*: no toast is on screen while the audit walks routes, so it
        // had never seen this button at all.
        className="relative shrink-0 -mr-1 p-1 rounded hover:bg-black/5 transition-colors after:absolute after:-inset-[11px] after:content-['']"
      >
        <X className="w-3.5 h-3.5" aria-hidden />
      </button>
    </div>
  );
}

/**
 * The message body — two lines by default, expandable to the whole thing.
 *
 * It used to be `truncate`: one line, ellipsis, no way to read the rest. A long
 * error was therefore both cut off *and* gone after 2.5s.
 *
 * `line-clamp-2` gives it two lines. Whether the text is *actually* overflowing
 * is a layout question, so it is measured (`scrollHeight > clientHeight`) — but
 * a measurement needs a layout engine, and jsdom has none (`clientHeight` and
 * `scrollHeight` are both 0 there). When the text cannot be measured it cannot
 * be clamped either, so the message is left fully readable and the affordance
 * is offered on the strength of the message's own length.
 */
function ToastMessage({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  /** `null` = not measurable (or not measured yet); `true` = overflowing. */
  const [overflowing, setOverflowing] = useState<boolean | null>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  // `useLayoutEffect`, not `useEffect`: measuring after paint means the toast
  // is painted un-clamped for one frame and then jumps to two lines.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) {
      setOverflowing(null);
      return;
    }
    const measurable = el.clientHeight > 0 && el.scrollHeight > 0;
    setOverflowing(measurable ? el.scrollHeight > el.clientHeight : null);
  }, [message, expanded]);

  const measurable = overflowing !== null;
  const clamped = overflowing === true;
  // `expanded` keeps the control alive so the message can be collapsed again.
  const expandable =
    expanded || clamped || (!measurable && message.length >= UNMEASURED_CLAMP_MIN_CHARS);
  const body = (
    <span
      ref={textRef}
      data-testid="toast-message"
      className={cn(
        "block",
        !expanded && "line-clamp-2",
        // The 「there is more」 cue is a **mask**, not an overlay: an overlay
        // would have to be painted in the background colour of the variant, and
        // there are six of those. A mask is colour-independent. The `_` in
        // `calc(100%_-_1rem)` is how a Tailwind arbitrary value spells the
        // spaces `calc()` requires around the `-`.
        expandable &&
          !expanded &&
          "mask-[linear-gradient(to_bottom,black_0,black_calc(100%_-_1rem),transparent_100%)]",
      )}
    >
      {message}
    </span>
  );

  return (
    <div className="flex-1 min-w-0">
      {expandable ? (
        <button
          type="button"
          data-testid="toast-expand"
          aria-expanded={expanded}
          // The name carries the message first (so the live region still
          // announces it) and then the affordance. It does **not** change when
          // the message expands: `aria-expanded` carries the state, and a name
          // that flips on every toggle would re-announce the whole card.
          aria-label={`${message}（展開完整訊息）`}
          onClick={() => setExpanded((v) => !v)}
          className="block w-full cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
        >
          {body}
        </button>
      ) : (
        body
      )}
    </div>
  );
}

// Allow non-component exports alongside <ToastProvider/> — `useToast` hook is
// intentionally co-located for the consumer DX (mirrors useAuth pattern in
// `AuthProvider.tsx`).
// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be called inside <ToastProvider>");
  }
  return ctx;
}
